import React, { useState, useEffect, useRef, useCallback } from "react";
import { apiFetch } from "../../../apiFetch";
import { useNavigate } from "react-router-dom";
import { FaPlay } from "react-icons/fa";
import "./Header.css";
import { rolePath } from "./roleApi";

const Header = ({ profileImage, onLogout, userId, role, title }) => {
    const [loadedImage, setLoadedImage] = useState(profileImage || null);
    const [notificationCount, setNotificationCount] = useState(0);
    const [notifications, setNotifications] = useState([]);
    const [prevUnreadCount, setPrevUnreadCount] = useState(0);
    const [showNotifications, setShowNotifications] = useState(false);
    const [isDropdownVisible, setDropdownVisible] = useState(false);
    const [hasInteracted, setHasInteracted] = useState(false);
    const [showBetaModal, setShowBetaModal] = useState(false);


    const dropdownRef = useRef(null);
    const notificationRef = useRef(null);
    const notificationAudioRef = useRef(null);

    const navigate = useNavigate();

    const normalizedRole = (role || "").toLowerCase();
    const safeRole = ["agent", "scout", "club"].includes(normalizedRole) ? normalizedRole : "agent";
    const cap = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : "");
    const resolvedTitle =
        title || `${cap(safeRole)} Dashboard`;


    const resolveEffectiveUserId = () => {
        if (userId) return userId;
        try {
            const storedRole =
                (role ||
                    (typeof window !== "undefined"
                        ? localStorage.getItem("role") || localStorage.getItem("userRole")
                        : "") ||
                    "").toLowerCase();

            const keyMap = {
                agent: "agentId",
                scout: "scoutId",
                club: "clubId",
            };

            const key = keyMap[storedRole];
            if (!key || typeof window === "undefined") return null;
            return localStorage.getItem(key);
        } catch {
            return null;
        }
    };

    const effectiveUserId = resolveEffectiveUserId();


    // 👤 PROFILE DROPDOWN TOGGLE
    const handleProfileClick = () => {
        setDropdownVisible((prev) => !prev);
    };

    // 🏆 LEADERBOARD NAVIGATION
    const handleLeaderboardClick = () => {
        navigate("/leaderboard");
    };


    // ▶️ FEED / FYP NAVIGATION
    const handleFeedClick = () => {
        const roleSlug =
            safeRole === "agent"
                ? "agent-dashboard"
                : safeRole === "scout"
                    ? "scout-dashboard"
                    : "club-dashboard";

        navigate(`/${roleSlug}/feed`);
    };




    const handlePaymentOptions = async () => {
        setDropdownVisible(false);
        setShowBetaModal(true);
        return;

        try {
            const res = await apiFetch("/api/billing/portal", {
                method: "POST",
            });

            if (!res.ok) {
                const err = await res.json().catch(() => ({}));
                console.error("Billing portal error:", err);
                return;
            }

            const data = await res.json();

            if (data.url) {
                window.location.href = data.url;
            }
        } catch (err) {
            console.error("❌ Failed to open billing portal:", err);
        }
    };


    const handleManageSubscription = () => {
        setDropdownVisible(false);
        setShowBetaModal(true);
        return;

        const roleSlug =
            safeRole === "agent"
                ? "agent-dashboard"
                : safeRole === "scout"
                    ? "scout-dashboard"
                    : "club-dashboard";

        navigate(`/${roleSlug}/subscription`);
    };





    useEffect(() => {
        if (profileImage) {
            setLoadedImage(profileImage);
        }
    }, [profileImage]);



    // 🧼 CLOSE DROPDOWN ON OUTSIDE CLICK
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (
                dropdownRef.current &&
                !dropdownRef.current.contains(event.target) &&
                !event.target.closest(".profile-container")
            ) {
                setDropdownVisible(false);
            }
        };

        document.addEventListener("click", handleClickOutside);
        return () => document.removeEventListener("click", handleClickOutside);
    }, []);


    useEffect(() => {
        notificationAudioRef.current = new Audio(
            "/ringtone-you-would-be-glad-to-know.mp3"
        );
    }, []);

    useEffect(() => {
        const markInteracted = () => setHasInteracted(true);
        window.addEventListener("click", markInteracted, { once: true });
        window.addEventListener("keydown", markInteracted, { once: true });
        return () => {
            window.removeEventListener("click", markInteracted);
            window.removeEventListener("keydown", markInteracted);
        };
    }, []);

    useEffect(() => {
        const fetchProfileImage = async () => {
            if (!effectiveUserId) return;

            const rolesToTry = [safeRole];


            for (const r of rolesToTry) {
                try {
                    const path = rolePath({ role: r, id: effectiveUserId }, "profile");
                    const res = await apiFetch(path);




                    if (!res.ok) continue;

                    const data = await res.json();
                    const img =
                        data.profileImage ||
                        data.profileImageUrl ||
                        data.photoUrl ||
                        data.avatar ||
                        data.avatarUrl ||
                        data.image ||
                        null;

                    if (img) {
                        setLoadedImage(img);
                        return;
                    }
                } catch { }
            }
        };

        fetchProfileImage();
    }, [effectiveUserId, safeRole]);


    const fetchNotifications = useCallback(async () => {
        if (!effectiveUserId) return;

        try {
            const response = await apiFetch(`/api/notifications/${safeRole}/${effectiveUserId}`);



            if (!response.ok) return;
            const payload = await response.json();
            const list = Array.isArray(payload)
                ? payload
                : Array.isArray(payload.items)
                    ? payload.items
                    : Array.isArray(payload.data)
                        ? payload.data
                        : [];

            const unread = list.filter((n) => !n.isRead);

            if (
                unread.length > prevUnreadCount &&
                hasInteracted &&
                notificationAudioRef.current
            ) {
                notificationAudioRef.current.play().catch(() => { });
            }

            setPrevUnreadCount(unread.length);
            setNotificationCount(unread.length);
            setNotifications(list);
        } catch (err) {
            console.error("❌ Error loading notifications:", err);
        }
    }, [effectiveUserId, safeRole, hasInteracted, prevUnreadCount]);


    useEffect(() => {
        fetchNotifications();
        const interval = setInterval(fetchNotifications, 10000);
        return () => clearInterval(interval);
    }, [fetchNotifications]);

    const handleNotificationClick = async () => {
        setShowNotifications((v) => !v);

        if (notificationCount > 0 && effectiveUserId) {
            try {
                await apiFetch(`/api/notifications/${safeRole}/${effectiveUserId}/read`, {
                    method: "PUT",
                });


                setNotificationCount(0);
                fetchNotifications();
            } catch { }
        }
    };

    const notificationsPath =
        safeRole === "agent"
            ? "/agent-dashboard/notifications"
            : safeRole === "scout"
                ? "/scout-dashboard/notifications"
                : "/club-dashboard/notifications";


    return (
        <header className="role-dashboard-header">
            <div className="logo-container">
                <img
                    src="/images/VSN.png"
                    alt="Logo"
                    className="logo-image"
                />
            </div>

            <div className="title">
                Virtual Scouting Network ({resolvedTitle})
            </div>

            <div className="header-icons">

                {/* Feed / FYP */}
                <div
                    className="feed-icon"
                    onClick={handleFeedClick}
                    title="Your Feed"
                >
                    <FaPlay />
                </div>



                {/* 🏆 Leaderboard */}
                <div
                    className="leaderboard-icon"
                    onClick={handleLeaderboardClick}
                    title="Leaderboard"
                >
                    🏆
                </div>

                {/* 🔔 Notifications */}
                <div
                    className="notification-bell-container"
                    onClick={handleNotificationClick}
                >
                    <span className="notification-bell">
                        🔔
                        {notificationCount > 0 && (
                            <span className="notification-count">
                                {notificationCount}
                            </span>
                        )}
                    </span>

                    {showNotifications && notifications.length > 0 && (
                        <div className="notification-dropdown" ref={notificationRef}>
                            <ul>
                                {notifications.map((note) => (
                                    <li
                                        key={note.id}
                                        className={note.isRead ? "read" : "unread"}
                                        onClick={() =>
                                            navigate(notificationsPath, {
                                                state: { notification: note },
                                            })
                                        }
                                    >
                                        <strong className="notification-title">
                                            {note.type}
                                        </strong>
                                        <br />
                                        <span className="notification-preview">
                                            {note.message}
                                        </span>

                                        {note.link && (
                                            <>
                                                <br />
                                                <button
                                                    type="button"
                                                    className="notification-link"
                                                    onClick={(e) => {
                                                        e.stopPropagation();

                                                        if (note.link.startsWith("/")) {
                                                            // internal app route → SPA navigation
                                                            navigate(note.link);
                                                        } else {
                                                            // external URL → open in new tab
                                                            window.open(note.link, "_blank", "noopener,noreferrer");
                                                        }
                                                    }}
                                                >
                                                    {note.linkLabel || "View"}
                                                </button>
                                            </>
                                        )}


                                        <br />
                                        <span className="notification-time">
                                            {new Date(note.timestamp).toLocaleString()}
                                        </span>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}
                </div>
            </div>


            <div
                className="profile-container"
                onClick={(e) => {
                    e.stopPropagation();
                    handleProfileClick();
                }}
            >

                {loadedImage ? (
                    <img
                        src={loadedImage}
                        alt="Profile"
                        className="header-profile-image"
                        onError={() => {
                            console.error(`Failed to load ${safeRole} header profile image:`, loadedImage);
                            setLoadedImage(null);
                        }}
                    />
                ) : (
                    <span className="default-profile" />
                )}
            </div>
            {isDropdownVisible && (
                <div
                    className="dropdown-menu"
                    ref={dropdownRef}
                    onClick={(e) => e.stopPropagation()}
                >

                    <ul>
                        <li
                            onClick={handlePaymentOptions}
                            style={{ cursor: "pointer" }}
                        >
                            Payment Options
                        </li>

                        <li
                            onClick={handleManageSubscription}
                            style={{ cursor: "pointer" }}
                        >
                            Manage Subscription
                        </li>

                        <li
                            onClick={onLogout}
                            style={{ cursor: "pointer" }}
                        >
                            Logout
                        </li>
                    </ul>

                </div>
            )}

            {showBetaModal && (
                <div className="player-profile-underage-modal-overlay">
                    <div
                        className="player-profile-underage-modal"
                        style={{
                            backgroundColor: "black",
                            border: "2px solid yellow",
                            borderRadius: "8px",
                            textAlign: "center",
                            padding: "20px",
                            maxWidth: "420px",
                            margin: "0 auto",
                        }}
                    >
                        <h2 style={{ color: "yellow", fontSize: "20px" }}>
                            🚧 Feature Not Available
                        </h2>

                        <p style={{ color: "white", fontSize: "15px", marginTop: "12px" }}>
                            Payment and subscription features are currently unavailable.
                            <br /><br />
                            The app is in <strong>Beta Testing Mode</strong>.
                        </p>

                        <button
                            onClick={() => setShowBetaModal(false)}
                            style={{
                                marginTop: "20px",
                                padding: "10px 40px",
                                backgroundColor: "yellow",
                                color: "black",
                                fontWeight: "bold",
                                border: "none",
                                borderRadius: "5px",
                                cursor: "pointer",
                            }}
                        >
                            Close
                        </button>
                    </div>
                </div>
            )}


        </header>
    );
};

export default Header;








